import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, ChevronRight, Star } from 'lucide-react';
import { trackEvent, trackAppDownload, trackLandingPageVisit, trackPageView } from '../services/analytics';
import { clearAllTimelineCaches } from '../utils/verifyUserData';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

/* ─── Star Arc ─── */
const StarArc = ({ className = '' }) => (
  <svg viewBox="0 0 200 55" className={className} style={{display:'block',margin:'0 auto'}}>
    <defs>
      <linearGradient id="aG" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#F3E5AB" stopOpacity=".9"/>
        <stop offset="50%" stopColor="#D4AF37" stopOpacity=".95"/>
        <stop offset="100%" stopColor="#B8962E" stopOpacity=".9"/>
      </linearGradient>
      <linearGradient id="aGstar" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%"   stopColor="#F7E6A8"/>
        <stop offset="55%"  stopColor="#D4AF37"/>
        <stop offset="100%" stopColor="#9E7820"/>
      </linearGradient>
      <filter id="sG"><feGaussianBlur stdDeviation="3" result="b"/>
        <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>
    </defs>
    <motion.path d="M 25 45 Q 60 14,100 8 T 175 45" stroke="url(#aG)" strokeWidth="1.5"
      fill="none" strokeLinecap="round" filter="url(#sG)"
      initial={{pathLength:0,opacity:0}} animate={{pathLength:1,opacity:1}} transition={{duration:.8,delay:.2}}/>
    {[{x:25,y:45,s:6},{x:62,y:18,s:8},{x:100,y:8,s:10},
      {x:138,y:18,s:8},{x:175,y:45,s:6}].map((st,i)=>(
      <motion.path key={i} fill="url(#aGstar)" filter="url(#sG)"
        d={`M ${st.x} ${st.y-st.s} L ${st.x+st.s*.35} ${st.y-st.s*.35} L ${st.x+st.s} ${st.y} L ${st.x+st.s*.35} ${st.y+st.s*.35} L ${st.x} ${st.y+st.s} L ${st.x-st.s*.35} ${st.y+st.s*.35} L ${st.x-st.s} ${st.y} L ${st.x-st.s*.35} ${st.y-st.s*.35} Z`}
        initial={{opacity:0,scale:0}} animate={{opacity:1,scale:1}} transition={{duration:.3,delay:.35+i*.08}}/>
    ))}
  </svg>
);

/* ─── Apple Store Icon ─── */
const AppleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

/* ─── Google Play Icon ─── */
const PlayStoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3.609 1.814L13.792 12 3.609 22.186a.996.996 0 01-.609-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.302 2.302-2.302 2.302L15.396 12l2.302-2.302zm-3.199-3.199l-8.635-8.635 10.937 6.333-2.302 2.302z"/>
  </svg>
);

/* ─── Realistic Planet ─── */
const Planet = ({ type, size, glow }) => {
  const s = size;
  const cm = {width:s,height:s,borderRadius:'50%',position:'absolute',inset:0};
  const surfaces = {
    earth:(<>
      <div style={{...cm,background:'radial-gradient(circle at 30% 30%,#3a8abf 0%,#1f6090 35%,#0e3d60 60%,#082438 80%)'}}/>
      <div style={{...cm,background:'radial-gradient(ellipse at 40% 35%,rgba(45,130,70,.4) 0%,transparent 28%),radial-gradient(ellipse at 60% 52%,rgba(45,130,70,.3) 0%,transparent 22%)'}}/>
      <div style={{...cm,boxShadow:'inset -3px -3px 8px rgba(0,0,0,.5)'}}/>
    </>),
    venus:(<>
      <div style={{...cm,background:'radial-gradient(circle at 30% 30%,#dcc090 0%,#b89860 30%,#907040 55%,#604828 80%)'}}/>
      <div style={{...cm,background:'linear-gradient(155deg,transparent 10%,rgba(220,200,160,.12) 30%,transparent 50%)'}}/>
      <div style={{...cm,boxShadow:'inset -3px -3px 7px rgba(0,0,0,.4)'}}/>
    </>),
    saturn:(<>
      <div style={{...cm,background:'radial-gradient(circle at 30% 30%,#d8c498 0%,#b8a070 30%,#907848 55%,#685030 80%)'}}/>
      <div style={{...cm,background:'linear-gradient(0deg,transparent 8%,rgba(160,130,70,.15) 18%,transparent 25%,rgba(180,150,90,.1) 38%,transparent 45%)'}}/>
      <div style={{...cm,boxShadow:'inset -3px -3px 7px rgba(0,0,0,.4)'}}/>
      <div style={{position:'absolute',width:s*1.85,height:s*.5,left:'50%',top:'50%',
        transform:'translate(-50%,-50%) rotateX(68deg)',zIndex:5,pointerEvents:'none'}}>
        <div style={{position:'absolute',inset:0,borderRadius:'50%',
          background:'radial-gradient(ellipse,transparent 42%,rgba(180,155,100,.35) 47%,rgba(190,165,110,.2) 54%,transparent 58%,rgba(165,140,85,.18) 63%,transparent 70%)'}}/>
      </div>
    </>),
    jupiter:(<>
      <div style={{...cm,background:'radial-gradient(circle at 30% 30%,#c8a078 0%,#a07850 30%,#785838 55%,#503820 80%)'}}/>
      <div style={{...cm,background:'linear-gradient(0deg,rgba(90,50,25,.2) 8%,transparent 15%,rgba(150,110,65,.15) 22%,transparent 30%,rgba(110,65,30,.18) 38%,transparent 46%)'}}/>
      <div style={{position:'absolute',width:'18%',height:'10%',top:'56%',left:'56%',borderRadius:'50%',background:'radial-gradient(ellipse,rgba(150,50,25,.3) 0%,transparent 65%)'}}/>
      <div style={{...cm,boxShadow:'inset -4px -4px 8px rgba(0,0,0,.45)'}}/>
    </>),
    moon:(<>
      <div style={{...cm,background:'radial-gradient(circle at 32% 32%,#c8c4be 0%,#a8a49e 30%,#888480 55%,#686460 80%)'}}/>
      <div style={{...cm,background:'radial-gradient(circle at 55% 40%,rgba(0,0,0,.08) 0%,transparent 10%),radial-gradient(circle at 35% 65%,rgba(0,0,0,.1) 0%,transparent 8%)'}}/>
      <div style={{...cm,boxShadow:'inset -3px -3px 6px rgba(0,0,0,.4)'}}/>
    </>),
    mercury:(<>
      <div style={{...cm,background:'radial-gradient(circle at 32% 32%,#a09890 0%,#807870 35%,#605850 60%,#403830 80%)'}}/>
      <div style={{...cm,boxShadow:'inset -3px -3px 5px rgba(0,0,0,.5)'}}/>
    </>),
    neptune:(<>
      <div style={{...cm,background:'radial-gradient(circle at 30% 30%,#4a78b8 0%,#2e5898 30%,#163878 55%,#0a2058 80%)'}}/>
      <div style={{...cm,background:'linear-gradient(170deg,transparent 15%,rgba(70,115,185,.1) 30%,transparent 48%)'}}/>
      <div style={{...cm,boxShadow:'inset -3px -3px 7px rgba(0,0,0,.5)'}}/>
    </>),
    mars:(<>
      <div style={{...cm,background:'radial-gradient(circle at 30% 30%,#c87058 0%,#a04838 30%,#783020 55%,#502010 80%)'}}/>
      <div style={{position:'absolute',width:'30%',height:'8%',top:'5%',left:'35%',borderRadius:'50%',background:'radial-gradient(ellipse,rgba(200,190,180,.15) 0%,transparent 65%)'}}/>
      <div style={{...cm,boxShadow:'inset -3px -3px 6px rgba(0,0,0,.45)'}}/>
    </>),
  };
  return (
    <div className="relative" style={{width:s,height:s}}>
      <div className="absolute rounded-full" style={{inset:'-20%',width:'140%',height:'140%',
        background:`radial-gradient(circle,${glow} 0%,transparent 70%)`,filter:'blur(3px)',opacity:.45}}/>
      <div className="absolute rounded-full overflow-visible" style={{inset:0,width:s,height:s}}>
        {surfaces[type]}
        <div style={{position:'absolute',width:'28%',height:'28%',top:'9%',left:'15%',borderRadius:'50%',
          background:'radial-gradient(circle,rgba(255,255,255,.15) 0%,transparent 70%)',zIndex:2}}/>
        <div style={{position:'absolute',inset:0,borderRadius:'50%',
          background:'linear-gradient(135deg,transparent 40%,rgba(0,0,0,.22) 65%,rgba(0,0,0,.45) 85%)',zIndex:3}}/>
      </div>
    </div>
  );
};

/* ─── Solar System — BIGGER, CLEARER, NO CLIPPING ─── */
const DOMAINS = [
  {name:'Health',    type:'earth',   glow:'rgba(90,158,200,.4)',  sz:1.0,  a:0},
  {name:'Marriage',  type:'venus',   glow:'rgba(212,176,120,.4)', sz:.95,  a:45},
  {name:'Career',    type:'saturn',  glow:'rgba(200,168,96,.4)',  sz:1.12, a:90},
  {name:'Wealth',    type:'jupiter', glow:'rgba(200,144,104,.4)', sz:1.2,  a:135},
  {name:'Family',    type:'moon',    glow:'rgba(184,180,174,.35)',sz:.82,  a:180},
  {name:'Education', type:'mercury', glow:'rgba(138,130,120,.35)',sz:.75,  a:225},
  {name:'Spiritual', type:'neptune', glow:'rgba(88,120,184,.4)',  sz:1.0,  a:270},
  {name:'Travel',    type:'mars',    glow:'rgba(192,112,88,.4)',  sz:.88,  a:315},
];

const SolarSystem = () => {
  const base = 36;
  const orbits = [18, 28, 40];
  const assign = [0,1,2,0,1,2,0,1];

  return (
    <div className="relative w-full aspect-square" data-testid="solar-system" style={{maxWidth:500,margin:'0 auto'}}>
      <div className="absolute" style={{inset:'12%',position:'relative',width:'76%',height:'76%'}}>
        {orbits.map((r,i)=>(
          <div key={i} className="absolute rounded-full" style={{
            width:`${r*2}%`,height:`${r*2}%`,top:`${50-r}%`,left:`${50-r}%`,
            border:`1px solid rgba(148,163,184,${.06+i*.02})`,
          }}/>
        ))}
        <div className="absolute flex items-center justify-center" style={{
          width:'18%',height:'18%',top:'41%',left:'41%',borderRadius:'50%',
          background:'radial-gradient(circle,rgba(94,234,212,.1) 0%,transparent 100%)',
        }}>
          <img src="/sage-final.png" alt="Sage" style={{width:'100%',height:'100%',objectFit:'contain',
            filter:'drop-shadow(0 0 12px rgba(94,234,212,.3))'}}/>
        </div>
        {DOMAINS.map((d,i)=>{
          const oR = orbits[assign[i]];
          const dur = 50 + assign[i]*14 + i*4;
          const pS = Math.round(base * d.sz);
          return (
            <div key={d.name} className="absolute" style={{
              width:`${oR*2}%`,height:`${oR*2}%`,top:`${50-oR}%`,left:`${50-oR}%`,
              animation:`sp${i} ${dur}s linear infinite`,
            }}>
              <style>{`
                @keyframes sp${i}{from{transform:rotate(${d.a}deg)}to{transform:rotate(${d.a+360}deg)}}
                @keyframes usp${i}{from{transform:rotate(-${d.a}deg)}to{transform:rotate(-${d.a+360}deg)}}
              `}</style>
              <div className="absolute" style={{top:0,left:'50%',transform:'translate(-50%,-50%)'}}>
                <div style={{animation:`usp${i} ${dur}s linear infinite`}}>
                  <div className="flex flex-col items-center" style={{gap:2}}>
                    <Planet type={d.type} size={pS} glow={d.glow}/>
                    <span style={{fontSize:10,fontFamily:"'Inter',sans-serif",fontWeight:600,
                      color:d.glow.replace(/[\d.]+\)$/,'1)'),whiteSpace:'nowrap',
                      textShadow:'0 1px 4px rgba(0,0,0,.95)',letterSpacing:'.02em'}}>{d.name}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─── Phone Mockup — Auto-scrolling App Screenshots ─── */
const PhoneMockup = () => {
  const [currentScreen, setCurrentScreen] = React.useState(0);
  const screens = [
    '/app-screens/00_splash_screen.jpg',
    '/app-screens/01_onboarding_form.jpg',
    '/app-screens/02_generating_story_20pct.jpg',
    '/app-screens/04_past_story_planets.jpg',
    '/app-screens/05_past_story_reading.jpg',
    '/app-screens/09_dashboard_hexstory.jpg',
    '/app-screens/13_life_insights.jpg',
    '/app-screens/15_future_reels_planets.jpg',
    '/app-screens/17_sage_qa_page.jpg',
    '/app-screens/21_payment_plans.jpg',
  ];

  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentScreen(prev => (prev + 1) % screens.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [screens.length]);

  return (
  <div className="relative" data-testid="phone-mockup" style={{width:'100%',maxWidth:260}}>
    {/* Cosmic glow behind phone */}
    <div className="absolute" style={{
      inset:'-12%',width:'124%',height:'124%',
      background:'radial-gradient(ellipse at center,rgba(94,234,212,.06) 0%,rgba(116,179,255,.04) 30%,rgba(167,139,250,.02) 50%,transparent 70%)',
      filter:'blur(20px)',pointerEvents:'none',zIndex:0,
    }}/>
    {/* Phone shell */}
    <div className="relative" style={{
      borderRadius:32,
      border:'2px solid rgba(94,234,212,.08)',
      background:'linear-gradient(180deg,#0d1220 0%,#0a0e17 100%)',
      padding:'10px 6px 10px',
      boxShadow:'0 24px 72px rgba(0,0,0,.5),0 0 40px rgba(94,234,212,.04),inset 0 1px 0 rgba(94,234,212,.04)',
      zIndex:1,
    }}>
      {/* Dynamic Island */}
      <div className="mx-auto mb-1.5" style={{
        width:80,height:22,borderRadius:12,
        background:'#050810',border:'1px solid rgba(94,234,212,.04)',
      }}/>
      {/* Screen with auto-scrolling screenshots */}
      <div style={{
        borderRadius:22,overflow:'hidden',
        background:'#0a0f1c',
        aspectRatio:'9/19.5',
        position:'relative',
      }}>
        {screens.map((src, idx) => (
          <img
            key={src}
            src={src}
            alt={`App screen ${idx + 1}`}
            style={{
              width:'100%',height:'100%',objectFit:'cover',display:'block',
              position: idx === 0 ? 'relative' : 'absolute',
              top:0,left:0,
              opacity: idx === currentScreen ? 1 : 0,
              transition:'opacity 0.8s ease-in-out',
            }}
          />
        ))}
        {/* Screen dots indicator */}
        <div style={{
          position:'absolute',bottom:8,left:'50%',transform:'translateX(-50%)',
          display:'flex',gap:4,zIndex:2,
        }}>
          {screens.map((_, idx) => (
            <div key={idx} style={{
              width: idx === currentScreen ? 16 : 5,height:5,borderRadius:3,
              background: idx === currentScreen ? 'rgba(94,234,212,0.8)' : 'rgba(255,255,255,0.3)',
              transition:'all 0.3s ease',
            }}/>
          ))}
        </div>
      </div>
      {/* Home bar */}
      <div className="mx-auto mt-1.5" style={{width:40,height:3,borderRadius:2,background:'rgba(94,234,212,.06)'}}/>
    </div>
  </div>
  );
};

/* ─── Floating Background Planets ─── */
const BgPlanet = ({ scrollPct, side }) => {
  const t = scrollPct/100;
  const baseX = side==='left'?5:88;
  const drift = Math.sin(t*Math.PI*2)*4;
  const top = 20+t*55;
  const hue = side==='left'?(175+t*50):(250+t*30);
  const c = `hsl(${hue},65%,45%)`;
  return (
    <>
      {/* Desktop */}
      <div className="fixed pointer-events-none z-0 hidden lg:block" style={{
        width:side==='left'?160:120,height:side==='left'?160:120,
        left:`${baseX+drift}%`,top:`${top}%`,
        transform:'translate(-50%,-50%)',transition:'left .5s ease-out,top .5s ease-out',
      }}>
        <div style={{width:'100%',height:'100%',borderRadius:'50%',
          background:`radial-gradient(circle at 35% 35%,rgba(255,255,255,.1),${c} 50%,hsl(${hue},75%,20%) 100%)`,
          boxShadow:`0 0 60px ${c}25`,opacity:.1,animation:'bgFloat 9s ease-in-out infinite',
        }}/>
      </div>
      {/* Mobile/Tablet */}
      <div className="fixed pointer-events-none z-0 lg:hidden" style={{
        width:side==='left'?90:70,height:side==='left'?90:70,
        left:`${baseX+drift}%`,top:`${top}%`,
        transform:'translate(-50%,-50%)',transition:'left .5s ease-out,top .5s ease-out',
      }}>
        <div style={{width:'100%',height:'100%',borderRadius:'50%',
          background:`radial-gradient(circle at 35% 35%,rgba(255,255,255,.08),${c} 50%,hsl(${hue},75%,20%) 100%)`,
          boxShadow:`0 0 40px ${c}20`,opacity:.08,animation:'bgFloat 9s ease-in-out infinite',
        }}/>
      </div>
    </>
  );
};

/* ─── Main ─── */
const WebsiteLandingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [stats, setStats] = useState({stories_generated:100000,users:12000,engines:16,accuracy_pct:92});
  const [scrollPct, setScrollPct] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const utmSource = searchParams.get('utm_source')||'direct';
    localStorage.setItem('utm_source',utmSource);
    localStorage.setItem('utm_medium',searchParams.get('utm_medium')||'');
    localStorage.setItem('utm_campaign',searchParams.get('utm_campaign')||'');
    trackEvent('watch_page_visit',{source:utmSource});
    fetch(`${API_URL}/api/public-stats`).then(r=>r.json()).then(d=>setStats(p=>({...p,...d}))).catch(()=>{});
  }, [searchParams]);

  useEffect(() => {
    const onScroll = () => {
      const el = containerRef.current;
      if(!el) return;
      setScrollPct(Math.min(100,Math.max(0,(window.scrollY/(el.scrollHeight-window.innerHeight))*100)));
    };
    window.addEventListener('scroll',onScroll,{passive:true});
    return ()=>window.removeEventListener('scroll',onScroll);
  }, []);

  const handleCTA = () => {
    const isEmergentDemo = location.pathname === '/watch1';
    trackEvent('watch_cta_click',{source: isEmergentDemo ? 'emergent' : 'watch'});

    // Mark this session as a "landing_flow" — downstream screens will show Download App CTAs
    // instead of Login/Skip/Continue buttons. Cleared when user explicitly moves forward
    // (Download or View All Domains click) or on native app / emergent-demo flows.
    if (!isEmergentDemo) localStorage.setItem('landing_flow', 'true');
    else localStorage.removeItem('landing_flow');

    // CRITICAL: Set/clear demo_source FIRST, before any early returns below.
    // Otherwise, a stale demo_source='emergent' flag from a prior /watch1 visit
    // sticks around and forces /watch downstream pages into emergent-demo mode
    // (showing LOGIN instead of Download App CTAs). See bug:
    // user lands on /loading-past-reels?source=landing from /watch but sees LOGIN.
    if (isEmergentDemo) localStorage.setItem('demo_source', 'emergent');
    else localStorage.removeItem('demo_source');

    // Preserve: device_id, past_story_viewed, free_story_used, cached story data
    const deviceId = localStorage.getItem('device_id');
    const taraanayaDeviceId = localStorage.getItem('taraanaya_device_id');
    const pastStoryViewed = localStorage.getItem('past_story_viewed');
    const freeStoryUsed = localStorage.getItem('free_story_used');
    const cachedStory = localStorage.getItem('unified_story_data');
    const cachedBirthDetails = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');

    // ─── BRANCH A: Returning visitor who already "moved forward" (viewed) ───
    // Replay cached story with Download App CTAs. Never navigate to /login.
    if (pastStoryViewed === 'true' || freeStoryUsed === 'true') {
      if (cachedStory) {
        localStorage.setItem('watching_story', 'true');
        navigate('/your-story?source=landing');
        return;
      }
      // No cache → fall through to onboarding
    }

    // ─── BRANCH B: Returning visitor who dropped off mid-flow (cached story + birth details exist, not yet viewed) ───
    // Resume their cached past story on the planets screen so they pick up where they left off.
    if (cachedStory && cachedBirthDetails && pastStoryViewed !== 'true' && freeStoryUsed !== 'true') {
      localStorage.setItem('watching_story', 'true');
      navigate('/loading-past-reels?source=landing');
      return;
    }

    // ─── BRANCH C: Fresh visitor (no cache) — full reset + onboarding ───
    const userDataKeys = [
      'onboarding_completed', 'story_generated',
      'story_generation_in_progress', 'unified_story_data', 'ai_story_data', 'prediction_id',
      'birthDetails', 'birth_details', 'audio_generation_complete', 'audio_prediction_id',
      'has_seen_past_story', 'past_reels_gen_state', 'watching_story', 'tts_currently_generating',
      'audio_ready_domains', 'domain_progress', 'domain_insights_cache', 'ai_life_insights',
      'audio_pregenerated', 'pregenerated_audio_data',
      // Future story data
      'unified_future_story', 'future_reels_ready', 'future_reels_generating',
      'future_reels_gen_start', 'future_story_language', 'domain_reel_positions',
      'domain_tts_status', 'tts_ultra_fast_complete', 'tts_user_id', 'tts_generation_started',
      'tts_generation_triggered', 'tts_retry_timestamp',
      // AI content + insights
      'ai_content_data', 'ai_content_english', 'ai_generation_in_progress',
      'domain_report_cache', 'content_versions',
      // Timeline data
      'detailed_yearly_report', 'detailed_monthly_data',
      'health_timeline', 'health_timeline_data', 'health_prediction_data', 'health_future_story_data',
      // User state
      'story_unlocked', 'story_cache_key', 'audio_user_id',
      'ps_selected_domain', 'ps_selected_language', 'ps_selected_mahadasha',
      'currentKernelUserId', 'currentPersonId', 'workflowData',
      'birth_session_id', 'completeBirthData', 'birthData', 'birthDate', 'birthTime',
      'birthPlace', 'placeOfBirth', 'birthCoordinates', 'gender',
    ];
    userDataKeys.forEach(k => localStorage.removeItem(k));
    clearAllTimelineCaches();

    // Restore preserved keys
    if (deviceId) localStorage.setItem('device_id', deviceId);
    if (taraanayaDeviceId) localStorage.setItem('taraanaya_device_id', taraanayaDeviceId);

    // Clear sessionStorage audio cache
    try {
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k?.startsWith('as_')) keysToRemove.push(k);
      }
      keysToRemove.forEach(k => sessionStorage.removeItem(k));
    } catch (e) {}
    if (isEmergentDemo) localStorage.setItem('demo_source', 'emergent');
    else localStorage.removeItem('demo_source');
    navigate(isEmergentDemo ? '/onboarding-new?source=emergent' : '/onboarding-new?source=watch');
  };

  const ENGINES = [
    'Vimshottari Dasha','Yogini Dasha','Chara Dasha','Ashtottari Dasha',
    'Kalachakra Dasha','Shodasvarga','Ashtakavarga','Transit Analysis',
    'Nakshatra Pada','Yoga Identification','Divisional Charts','Shadbala Strength',
    'Bhava Analysis','Mahadasha Map','Antardasha Map','Pratyantar Map',
  ];

  const T = {
    brand:{fontFamily:"'Dancing Script',cursive",fontSize:'clamp(32px,5.5vw,56px)',fontWeight:600,lineHeight:1.1},
    h1:{fontFamily:"'Inter',sans-serif",fontSize:'clamp(20px,3.5vw,44px)',fontWeight:700,lineHeight:1.15,letterSpacing:'-.02em'},
    h3:{fontFamily:"'Inter',sans-serif",fontSize:'clamp(15px,2vw,21px)',fontWeight:600,lineHeight:1.3},
    body:{fontFamily:"'Inter',sans-serif",fontSize:'clamp(13px,1.5vw,16px)',fontWeight:400,lineHeight:1.7},
    small:{fontFamily:"'Inter',sans-serif",fontSize:'clamp(11px,1.3vw,13px)',fontWeight:500,lineHeight:1.5},
    label:{fontFamily:"'Inter',sans-serif",fontSize:'clamp(9px,1vw,12px)',fontWeight:600,letterSpacing:'.15em',textTransform:'uppercase'},
    stat:{fontFamily:"'Inter',sans-serif",fontSize:'clamp(18px,2.8vw,30px)',fontWeight:700,lineHeight:1.2},
  };
  const C = {white:'#F1F5F9',mid:'#94A3B8',dim:'#64748B',accent:'#5EEAD4'};

  return (
    <div ref={containerRef} className="min-h-screen relative overflow-x-hidden"
      style={{background:'linear-gradient(180deg,#0F172A 0%,#0C1220 40%,#111827 100%)'}}
      data-testid="website-landing-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;500;600;700&family=Inter:wght@300;400;500;600;700&display=swap');
        @keyframes bgFloat{0%,100%{transform:translateY(0) scale(1)}50%{transform:translateY(-10px) scale(1.02)}}
        @keyframes ctaP{0%,100%{box-shadow:0 0 20px rgba(94,234,212,.25),0 0 40px rgba(116,179,255,.12)}50%{box-shadow:0 0 36px rgba(94,234,212,.45),0 0 72px rgba(116,179,255,.2)}}
        .cta-glow{animation:ctaP 2.5s ease-in-out infinite}
        .gc{background:rgba(255,255,255,.035);backdrop-filter:blur(14px);border:1px solid rgba(148,163,184,.1);transition:all .2s}
        .gc:hover{border-color:rgba(148,163,184,.18);background:rgba(255,255,255,.055)}
        @media(max-width:639px){
          .mobile-solar{transform:scale(0.78);transform-origin:center top;margin-top:-12px;margin-bottom:-24px}
        }
      `}</style>

      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none z-0" style={{
        background:'radial-gradient(ellipse 80% 50% at 50% 18%,rgba(94,234,212,.06) 0%,rgba(116,179,255,.03) 40%,transparent 70%)'}}/>

      {/* Floating particles */}
      <div className="fixed inset-0 pointer-events-none z-0">
        {Array.from({length:14}).map((_,i)=>(
          <motion.div key={i} className="absolute rounded-full" style={{
            width:Math.random()*2+1,height:Math.random()*2+1,
            background:i%3===0?'#5EEAD4':i%3===1?'#74B3FF':'#A78BFA',
            left:`${Math.random()*100}%`,top:`${Math.random()*100}%`,
          }} animate={{y:[0,-14,0],opacity:[.1,.45,.1]}}
            transition={{duration:3+Math.random()*3,repeat:Infinity,delay:Math.random()*2,ease:'easeInOut'}}/>
        ))}
      </div>

      {/* Background floating planets */}
      <BgPlanet scrollPct={scrollPct} side="left"/>
      <BgPlanet scrollPct={scrollPct} side="right"/>

      {/* Content */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-12">

        {/* ═══ HEADER ═══ */}
        <header className="pt-3 sm:pt-5 pb-1 sm:pb-2 flex items-center justify-between" data-testid="header-nav">
          <a href="/" data-testid="brand-link" style={{display:'inline-flex',flexDirection:'column',alignItems:'center',gap:'2px',textDecoration:'none'}}>
            <StarArc className="w-9 sm:w-12"/>
            <span style={{
              fontFamily:"'Dancing Script', cursive",
              fontSize:'clamp(20px,2.4vw,28px)',
              fontWeight:600,
              backgroundImage:'linear-gradient(90deg,#F3E5AB 0%,#D4AF37 50%,#B8962E 100%)',
              WebkitBackgroundClip:'text',
              backgroundClip:'text',
              WebkitTextFillColor:'transparent',
              color:'transparent',
              lineHeight:1,
              letterSpacing:'0.02em',
              marginTop:'-4px',
            }}>taraanaya</span>
          </a>
          {/* Web-First Launch: App Store / Play Store badges removed from header.
              Mobile apps will be available soon — see footer note below. */}
          <div className="flex items-center gap-1.5 sm:gap-2" data-testid="header-coming-soon">
            <span style={{
              fontSize:'clamp(9px,1.4vw,11px)',
              color:'rgba(243,229,171,0.55)',
              letterSpacing:'0.18em',
              textTransform:'uppercase',
              fontWeight:500,
              padding:'6px 10px',
              border:'1px solid rgba(243,229,171,0.18)',
              borderRadius:'999px',
              background:'rgba(243,229,171,0.04)',
            }}>iOS &middot; Android &mdash; Soon</span>
          </div>
        </header>

        {/* ═══ HERO — brand centered, then split: headline left + phone right ═══ */}
        <section className="pt-4 sm:pt-10 lg:pt-14" data-testid="hero-section">
          {/* Brand + pill centered */}
          <motion.div initial={{opacity:0,y:18}} animate={{opacity:1,y:0}} transition={{duration:.6}}
            className="text-center max-w-2xl mx-auto mb-3 sm:mb-8">
            <div className="mb-1 sm:mb-2"><StarArc className="w-20 sm:w-36 lg:w-42 mx-auto"/></div>
            <h2 data-testid="brand-name" className="mb-2 sm:mb-3" style={{
              fontFamily:"'Dancing Script', cursive",
              fontWeight:600,
              fontSize:'clamp(56px,12vw,128px)',
              lineHeight:1.0,
              letterSpacing:'0.01em',
              backgroundImage:'linear-gradient(135deg,#F7E6A8 0%,#F3E5AB 25%,#D4AF37 55%,#B8962E 85%,#9E7820 100%)',
              WebkitBackgroundClip:'text',
              backgroundClip:'text',
              WebkitTextFillColor:'transparent',
              color:'transparent',
              filter:'drop-shadow(0 0 32px rgba(212,175,55,0.32))',
            }}>taraanaya</h2>
            <div className="inline-flex items-center px-2.5 sm:px-3 py-1 rounded-full"
              style={{background:'rgba(243,229,171,.06)',border:'1px solid rgba(243,229,171,.18)'}}>
              <span style={{
                fontFamily:"'Cormorant Garamond', serif",
                color:'rgba(243,229,171,0.8)',
                fontSize:'clamp(9px,1.2vw,12px)',
                fontWeight:500,
                letterSpacing:'0.3em',
                textTransform:'uppercase',
              }}>Decision Intelligence &bull; Vedic AI</span>
            </div>
          </motion.div>

          {/* Split layout: Headline+CTA LEFT | Phone Demo RIGHT */}
          <div className="flex flex-col lg:flex-row items-center gap-3 sm:gap-6 lg:gap-10 pb-3 sm:pb-6 lg:pb-10" data-testid="hero-split">
            {/* Left: Headline + CTA */}
            <motion.div initial={{opacity:0,y:14}} animate={{opacity:1,y:0}} transition={{duration:.6,delay:.15}}
              className="flex-1 text-center lg:text-left">
              <h1 className="mb-2 sm:mb-4" style={{
                fontFamily:"'Cormorant Garamond', serif",
                color:'#F3E5AB',
                fontWeight:300,
                fontSize:'clamp(26px,5.2vw,52px)',
                lineHeight:1.15,
                letterSpacing:'0.04em',
              }} data-testid="hero-headline">
                Your Past Decoded.<br/>
                <span style={{
                  fontStyle:'italic',
                  color:'#92FFF7',
                  letterSpacing:'0.02em',
                }}>Your Future Revealed.</span>
              </h1>
              <p className="mb-4 sm:mb-6 max-w-lg mx-auto lg:mx-0" style={{...T.body,color:C.mid}}>
                16 ancient Vedic algorithms — refined over 5,000 years and built with India's finest astrologers — analyze your birth chart across 8 life domains.
              </p>
              <div className="max-w-[260px] sm:max-w-xs mx-auto lg:mx-0">
                <motion.button onClick={handleCTA} data-testid="hero-cta-btn"
                  className="cta-glow w-full py-3 sm:py-4 rounded-full flex items-center justify-center gap-2 active:scale-[0.97]"
                  style={{
                    background:'linear-gradient(135deg, #F3E5AB 0%, #D4AF37 60%, #B8962E 100%)',
                    color:'#0F172A',
                    fontSize:'clamp(13px,1.8vw,16px)',
                    fontFamily:"'Cormorant Garamond', serif",
                    fontWeight:500,
                    letterSpacing:'0.2em',
                    textTransform:'uppercase',
                    border:'1px solid rgba(243,229,171,0.45)',
                    boxShadow:'0 0 28px rgba(212,175,55,0.35)',
                  }}
                  whileTap={{scale:.97}}>
                  <Play size={15} fill="#0F172A"/> Watch Your Story
                </motion.button>
                <p className="mt-1.5 sm:mt-2" style={{
                  color:'rgba(243,229,171,0.55)',
                  fontSize:'clamp(9px,1.1vw,11px)',
                  letterSpacing:'0.14em',
                  textTransform:'uppercase',
                  fontWeight:500,
                }}>No signup · 2 minutes · Free</p>
              </div>
            </motion.div>
            {/* Right: Phone Demo */}
            <motion.div initial={{opacity:0,y:14}} animate={{opacity:1,y:0}} transition={{duration:.6,delay:.3}}
              className="flex-shrink-0 flex justify-center w-full lg:w-auto" style={{maxWidth:220}}>
              <PhoneMockup/>
            </motion.div>
          </div>

          {/* Solar System — full width, centered, compact on mobile */}
          <motion.div initial={{opacity:0,scale:.95}} whileInView={{opacity:1,scale:1}} viewport={{once:true}}
            transition={{duration:.5}} className="flex justify-center pb-3 sm:pb-6 lg:pb-10 mobile-solar" data-testid="domains-section">
            <SolarSystem/>
          </motion.div>
        </section>

        {/* ═══ STATS ═══ */}
        <section className="py-3 sm:py-5 lg:py-8" data-testid="trust-stats">
          <div className="grid grid-cols-4 gap-1.5 sm:gap-3 max-w-3xl mx-auto">
            {[{val:'1L+',smVal:'1,00,000+',label:'Charts Analyzed'},{val:'16',label:'Algorithms'},
              {val:'8',label:'Life Domains'},{val:'92%',label:'Accuracy'}].map((s,i)=>(
              <div key={i} className="gc rounded-xl py-2.5 sm:py-4 px-1 sm:px-2 text-center" data-testid={`stat-${i}`}>
                <div className="hidden sm:block" style={{...T.stat,color:C.white}}>{s.smVal||s.val}</div>
                <div className="sm:hidden" style={{...T.stat,color:C.white,fontSize:'clamp(16px,4.5vw,22px)'}}>{s.val}</div>
                <div className="mt-0.5" style={{...T.label,color:C.dim,fontSize:'clamp(7px,.9vw,10px)'}}>{s.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ═══ HOW IT WORKS ═══ */}
        <section className="py-5 sm:py-8 lg:py-12" data-testid="how-it-works-section">
          <div className="max-w-2xl mx-auto">
            <h3 className="mb-4 sm:mb-8 text-center" style={{...T.label,color:C.accent}}>How It Works</h3>
            <div className="flex flex-col gap-4 sm:gap-8">
              {[{n:'1',t:'Enter your birth details',d:'Name, date, time and place of birth. Takes 30 seconds.'},
                {n:'2',t:'16 algorithms analyze your Kundli',d:'Vimshottari, Yogini, Ashtottari, Kalachakra and 12 more run simultaneously.'},
                {n:'3',t:'Receive your life story',d:'Past verified. Present explained. Future predicted — in Hindi, Hinglish or English.'},
              ].map((step,i)=>(
                <motion.div key={i} initial={{opacity:0,y:10}} whileInView={{opacity:1,y:0}} viewport={{once:true}}
                  transition={{delay:i*.08}} className="flex items-start gap-4">
                  <div className="shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center"
                    style={{background:'linear-gradient(135deg,rgba(94,234,212,.1),rgba(116,179,255,.06))',border:'1px solid rgba(94,234,212,.18)'}}>
                    <span style={{fontSize:'clamp(13px,1.8vw,16px)',fontWeight:700,color:C.accent}}>{step.n}</span>
                  </div>
                  <div className="flex-1">
                    <h4 className="mb-0.5" style={{...T.h3,color:C.white,fontSize:'clamp(14px,1.8vw,18px)'}}>{step.t}</h4>
                    <p style={{...T.small,color:C.mid}}>{step.d}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ 16 ALGORITHMS ═══ */}
        <section className="py-5 sm:py-8 lg:py-12" data-testid="ai-engines-section">
          <h3 className="mb-1 text-center" style={{...T.label,color:C.accent}}>Ancient Science, Modern AI</h3>
          <p className="mb-3 sm:mb-6 text-center" style={{...T.h3,color:C.white}}>16 Vedic Algorithms</p>
          <div className="grid grid-cols-4 sm:grid-cols-4 lg:grid-cols-8 gap-1 sm:gap-2 max-w-4xl mx-auto">
            {ENGINES.map((e,i)=>(
              <motion.div key={e} initial={{opacity:0,scale:.9}} whileInView={{opacity:1,scale:1}} viewport={{once:true}}
                transition={{delay:i*.02}} className="gc rounded-lg py-2 sm:py-2.5 px-1.5 text-center"
                style={{...T.label,color:C.mid,fontSize:'clamp(8px,.9vw,10px)',letterSpacing:'.06em'}}>{e}</motion.div>
            ))}
          </div>
          <p className="text-center mt-4 max-w-md mx-auto" style={{...T.small,color:C.dim,fontSize:'clamp(10px,1.1vw,12px)'}}>
            Rooted in Parashara, Jaimini & Nadi traditions — validated against 1,00,000+ real birth charts.
          </p>
        </section>

        {/* ═══ SOCIAL PROOF ═══ */}
        <section className="py-4 sm:py-8 lg:py-12" data-testid="social-proof-section">
          <div className="gc rounded-2xl p-4 sm:p-7 text-center max-w-xl mx-auto">
            <div className="flex justify-center gap-0.5 mb-3">
              {[1,2,3,4,5].map(i=><Star key={i} size={14} style={{color:'#FBBF24',fill:'#FBBF24'}}/>)}
            </div>
            <p className="italic mb-3" style={{...T.body,color:'#CBD5E1',fontSize:'clamp(13px,1.6vw,16px)',lineHeight:1.7}}>
              "Mujhe vishwas nahi hua ki meri career prediction itni accurate thi. App ne meri job change 6 mahine pehle bata diya — exact same timeline."
            </p>
            <p style={{...T.label,color:C.dim,fontSize:'10px'}}>— Verified User, Delhi</p>
          </div>
        </section>

        {/* ═══ CREDIBILITY ═══ */}
        <section className="py-3 sm:py-6 lg:py-10" data-testid="credibility-section">
          <div className="max-w-xl mx-auto text-center">
            <p style={{...T.small,color:C.mid,lineHeight:1.7}}>
              Built with <span style={{color:C.white,fontWeight:600}}>India's top Vedic astrologers</span> and <span style={{color:C.white,fontWeight:600}}>AI engineers</span>.
              Every prediction cross-verified across multiple Dasha systems. Ancient wisdom. Modern precision.
            </p>
          </div>
        </section>

        {/* ═══ FINAL CTA ═══ */}
        <section className="py-6 sm:py-10 lg:py-16 text-center" data-testid="final-cta-section">
          <div className="max-w-xl mx-auto">
            <h3 className="mb-2 sm:mb-3" style={{
              fontFamily:"'Cormorant Garamond', serif",
              color:'#F3E5AB',
              fontWeight:300,
              fontSize:'clamp(24px,4vw,44px)',
              letterSpacing:'0.04em',
              lineHeight:1.2,
            }}>
              Your Stars Have a<br/>
              <span style={{
                fontStyle:'italic',
                color:'#92FFF7',
                letterSpacing:'0.02em',
              }}>Story to Tell</span>
            </h3>
            <p className="mb-4 sm:mb-8" style={{...T.body,color:C.dim}}>
              Join 12,000+ people who discovered their life blueprint through Vedic AI.
            </p>
            <div className="max-w-[300px] sm:max-w-sm mx-auto">
              <motion.button onClick={handleCTA} data-testid="final-cta-btn"
                className="cta-glow w-full py-3 sm:py-4 rounded-full flex items-center justify-center gap-2 active:scale-[0.97]"
                style={{
                  background:'linear-gradient(135deg, #F3E5AB 0%, #D4AF37 60%, #B8962E 100%)',
                  color:'#0F172A',
                  fontSize:'clamp(13px,1.8vw,16px)',
                  fontFamily:"'Cormorant Garamond', serif",
                  fontWeight:500,
                  letterSpacing:'0.22em',
                  textTransform:'uppercase',
                  border:'1px solid rgba(243,229,171,0.45)',
                  boxShadow:'0 0 28px rgba(212,175,55,0.35)',
                }}
                whileTap={{scale:.97}}>
                Begin Your Journey <ChevronRight size={16}/>
              </motion.button>
            </div>
          </div>
        </section>

        <footer className="pt-8 pb-10 sm:pt-12 sm:pb-14" data-testid="site-footer" style={{borderTop:'1px solid rgba(148,163,184,.08)',marginTop:'48px'}}>
          {/* Top row: brand + nav */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-5 mb-6 px-2">
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:'2px'}}>
              <StarArc className="w-10"/>
              <span style={{
                fontFamily:"'Dancing Script', cursive",
                fontSize:'24px',
                fontWeight:600,
                backgroundImage:'linear-gradient(90deg,#F3E5AB 0%,#D4AF37 50%,#B8962E 100%)',
                WebkitBackgroundClip:'text',
                backgroundClip:'text',
                WebkitTextFillColor:'transparent',
                color:'transparent',
                lineHeight:1,
                marginTop:'-4px',
              }}>taraanaya</span>
            </div>
            <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2" data-testid="footer-nav">
              {[
                {href:'/about',label:'About'},
                {href:'/pricing',label:'Pricing'},
                {href:'/privacy-policy',label:'Privacy'},
                {href:'/terms-of-service',label:'Terms'},
                {href:'/refund-policy',label:'Refund'},
                {href:'/contact',label:'Contact'},
              ].map(l=>(
                <a key={l.href} href={l.href} style={{
                  fontFamily:"'Inter',sans-serif",
                  fontSize:'12.5px',
                  letterSpacing:'0.08em',
                  color:'rgba(243,229,171,0.7)',
                  textTransform:'uppercase',
                  textDecoration:'none',
                  borderBottom:'1px solid transparent',
                  paddingBottom:'2px',
                  transition:'all 0.2s',
                }} onMouseEnter={e=>e.currentTarget.style.color='#F3E5AB'} onMouseLeave={e=>e.currentTarget.style.color='rgba(243,229,171,0.7)'}>{l.label}</a>
              ))}
            </nav>
          </div>

          {/* Contact strip */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 mb-5" style={{fontFamily:"'Inter',sans-serif",fontSize:'12px',color:'rgba(148,163,184,0.75)'}}>
            <a href="mailto:taraanaya@taraanaya.ai" data-testid="footer-email" style={{color:'rgba(243,229,171,0.85)',textDecoration:'none'}}>taraanaya@taraanaya.ai</a>
            <span className="hidden sm:inline" style={{opacity:0.4}}>|</span>
            <span>Mon–Sat &nbsp;·&nbsp; 10:00&nbsp;AM – 7:00&nbsp;PM IST</span>
          </div>

          {/* Legal block */}
          <div className="text-center px-2" style={{fontFamily:"'Inter',sans-serif",fontSize:'11px',lineHeight:1.7,color:'rgba(148,163,184,0.55)'}}>
            <div style={{color:'rgba(255,255,255,0.7)',fontWeight:500,fontSize:'11.5px',letterSpacing:'0.02em'}}>
              Taraanaya Vedtech Private Limited
            </div>
            <div>CIN: U96906UW2026PTC249734 &nbsp;|&nbsp; Registered with Central Registration Centre (RoC, MCA)</div>
            <div>Regd. Office: C-3/101, 1st Floor, Tower C3, Cleo County, Sector 121, Noida, Gautam Buddha Nagar, Uttar Pradesh – 201301, India</div>
            <div style={{marginTop:'8px',color:'rgba(148,163,184,0.45)',fontSize:'10.5px'}}>
              © 2026 Taraanaya Vedtech Private Limited. All rights reserved.
            </div>
          </div>
        </footer>
      </div>

      {/* FloatingContact handles WhatsApp + Phone globally */}
    </div>
  );
};

export default WebsiteLandingPage;
